import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Question, QuizMode, SessionRecord } from '../types';
import { AnswerButton, ARABIC_LETTERS, ENGLISH_LETTERS } from './AnswerButton';
import type { AnswerState } from './AnswerButton';
import { AudioControls, buildQuestionSequence } from './AudioControls';
import { Bi } from './Bilingual';
import { QuestionImage } from './QuestionImage';
import { SpeakButton } from './SpeakButton';
import { AppHeader, EmptyState, Fraction, ProgressBar, VoiceWarning } from './common';
import { useT } from '../hooks/useT';
import { CATEGORY_BY_ID } from '../data/categories';
import { useProgress } from '../state/ProgressContext';
import { useRouter } from '../state/RouterContext';
import { useSettings } from '../state/SettingsContext';
import { useTts } from '../state/TtsContext';

export interface QuizConfig {
  /** Show ✅/❌ as soon as an answer is tapped (Learning Mode). */
  immediateFeedback: boolean;
  /** Keep the learner on the question until they get it right. */
  allowRetry: boolean;
  /** Require a Submit press, then reveal (Practice Test). */
  requireSubmit: boolean;
  /** Offer the "Why?" explanation. */
  showExplanation: boolean;
  /** Read the question and all answers automatically (Listen & Answer). */
  autoPlayAll: boolean;
  /** Practice test only. */
  passThreshold?: number;
  /** Which progress table to write to. */
  kind: 'question' | 'sign';
  /** Show the running correct/incorrect counters. */
  showCounters: boolean;
}

interface QuizRunnerProps {
  mode: QuizMode;
  title: string;
  subtitle?: string;
  questions: Question[];
  config: QuizConfig;
  categoryId?: Question['category'];
  emptyMessage?: string;
}

interface AnswerRecord {
  questionId: string;
  chosenIndex: number | null;
  correct: boolean;
}

const PRAISE_AR = ['أحسنت!', 'ممتاز!', 'عمل رائع!', 'إجابة صحيحة!'];
const PRAISE_EN = ['Well done!', 'Excellent!', 'Great job!', 'Correct!'];

export function QuizRunner({
  mode,
  title,
  subtitle,
  questions,
  config,
  categoryId,
  emptyMessage,
}: QuizRunnerProps) {
  const t = useT();
  const { settings, uiLang } = useSettings();
  const { answer: recordAnswer, finishSession } = useProgress();
  const { speak, speakSequence, stop, pause, resume, status, canSpeak, speechLang } = useTts();
  const { home } = useRouter();

  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [wrongTried, setWrongTried] = useState<number[]>([]);
  const [revealed, setRevealed] = useState(false);
  const [solved, setSolved] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [finished, setFinished] = useState(false);
  const [results, setResults] = useState<AnswerRecord[]>([]);

  const startedAt = useRef<number>(Date.now());
  const recordedFirstTry = useRef<Set<string>>(new Set());
  const sessionSaved = useRef(false);
  const resultsRef = useRef<AnswerRecord[]>([]);
  resultsRef.current = results;
  /** Mirrors `index` synchronously so a fast double-tap on Next cannot skip
   *  past the last question (state updates are batched, a ref is not). */
  const indexRef = useRef(0);

  const question = questions[index];
  const total = questions.length;
  const letters = uiLang === 'ar' ? ARABIC_LETTERS : ENGLISH_LETTERS;

  const correctCount = results.filter((r) => r.correct).length;
  const wrongCount = results.length - correctCount;

  /* ------------------------------------------------------------------ */
  /* Audio                                                               */
  /* ------------------------------------------------------------------ */

  const sequence = useMemo(() => {
    if (!question) return [];
    const useArabic = speechLang === 'ar';
    return buildQuestionSequence(
      useArabic ? question.questionAr : question.questionEn,
      useArabic ? question.answersAr : question.answersEn,
      speechLang,
    );
  }, [question, speechLang]);

  // Read the new question aloud when it appears.
  useEffect(() => {
    if (!question || !canSpeak) return;
    if (config.autoPlayAll) {
      const timer = window.setTimeout(() => speakSequence(sequence), 350);
      return () => window.clearTimeout(timer);
    }
    if (settings.autoSpeakQuestion) {
      const text = speechLang === 'ar' ? question.questionAr : question.questionEn;
      const timer = window.setTimeout(() => speak(text, speechLang, 'question'), 350);
      return () => window.clearTimeout(timer);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question?.id, config.autoPlayAll, settings.autoSpeakQuestion, canSpeak, speechLang]);

  // Never leave audio running when this screen goes away.
  useEffect(() => () => stop(), [stop]);

  /* ------------------------------------------------------------------ */
  /* Session bookkeeping                                                 */
  /* ------------------------------------------------------------------ */

  /**
   * `completed` separates "reached the end" from "walked away half way".
   * A test that was abandoned still records the practice that was done, but it
   * is not scored and does not count as a test taken — otherwise closing the
   * screen would show up in the caregiver report as a failed test.
   */
  const saveSession = useCallback(
    (completed: boolean) => {
      if (sessionSaved.current) return;
      const answered = resultsRef.current.length;
      if (answered === 0) return;
      sessionSaved.current = true;
      const correct = resultsRef.current.filter((r) => r.correct).length;
      const record: SessionRecord = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        mode,
        categoryId,
        startedAt: startedAt.current,
        endedAt: Date.now(),
        answered,
        correct,
        wrong: answered - correct,
      };
      if (mode === 'test' && completed) {
        record.score = correct;
        record.total = total;
        record.passed = correct >= (config.passThreshold ?? Math.ceil(total * 0.75));
      }
      finishSession(record);
    },
    [mode, categoryId, total, config.passThreshold, finishSession],
  );

  // If the learner leaves half way through, still record what they did.
  useEffect(() => () => saveSession(false), [saveSession]);

  /* ------------------------------------------------------------------ */
  /* Answering                                                           */
  /* ------------------------------------------------------------------ */

  const speakFeedback = useCallback(
    (isCorrect: boolean) => {
      if (!settings.speakFeedback || !canSpeak) return;
      const text =
        speechLang === 'ar'
          ? isCorrect
            ? 'إجابة صحيحة'
            : 'حاول مرة أخرى'
          : isCorrect
            ? 'Correct'
            : 'Try again';
      window.setTimeout(() => speak(text, speechLang, 'feedback'), 120);
    },
    [settings.speakFeedback, canSpeak, speechLang, speak],
  );

  const commitResult = useCallback(
    (q: Question, chosenIndex: number | null, isCorrect: boolean) => {
      if (recordedFirstTry.current.has(q.id)) return;
      recordedFirstTry.current.add(q.id);
      recordAnswer(config.kind, q.id, isCorrect);
      setResults((prev) => [...prev, { questionId: q.id, chosenIndex, correct: isCorrect }]);
    },
    [recordAnswer, config.kind],
  );

  const handleSelect = (choice: number) => {
    if (!question) return;

    // Practice test: choosing only selects. Nothing is revealed yet.
    if (config.requireSubmit && !revealed) {
      setSelected(choice);
      return;
    }
    if (solved || revealed) return;

    const isCorrect = choice === question.correctIndex;
    setSelected(choice);

    if (isCorrect) {
      const firstTry = wrongTried.length === 0;
      commitResult(question, choice, firstTry);
      setSolved(true);
      setRevealed(true);
      speakFeedback(true);
    } else {
      commitResult(question, choice, false);
      setWrongTried((prev) => (prev.includes(choice) ? prev : [...prev, choice]));
      speakFeedback(false);
      if (!config.allowRetry) {
        setRevealed(true);
      } else if (wrongTried.length >= 1) {
        setShowExplanation(true);
      }
    }
  };

  const handleSubmit = () => {
    if (!question || selected === null) return;
    const isCorrect = selected === question.correctIndex;
    commitResult(question, selected, isCorrect);
    setRevealed(true);
    setSolved(isCorrect);
    speakFeedback(isCorrect);
  };

  const goNext = useCallback(() => {
    stop();
    const current = indexRef.current;
    if (current + 1 >= total) {
      setFinished(true);
      saveSession(true);
      return;
    }
    indexRef.current = current + 1;
    setIndex(current + 1);
    setSelected(null);
    setWrongTried([]);
    setRevealed(false);
    setSolved(false);
    setShowExplanation(false);
  }, [total, stop, saveSession]);

  /* ------------------------------------------------------------------ */
  /* Keyboard                                                            */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName ?? '';
      const typing = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
      if (typing || finished || !question) return;

      const key = event.key.toLowerCase();

      if (/^[1-9]$/.test(key)) {
        const idx = Number(key) - 1;
        if (idx < question.answersEn.length) {
          event.preventDefault();
          handleSelect(idx);
        }
        return;
      }
      if (key === 'r' || (event.key === ' ' && tag !== 'BUTTON' && tag !== 'A')) {
        event.preventDefault();
        speakSequence(sequence);
        return;
      }
      if (key === 's') {
        event.preventDefault();
        stop();
        return;
      }
      if (key === 'p') {
        event.preventDefault();
        if (status.state === 'speaking') pause();
        else if (status.state === 'paused') resume();
        return;
      }
      if (event.key === 'Enter' && tag !== 'BUTTON' && (solved || revealed)) {
        event.preventDefault();
        goNext();
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question, finished, sequence, solved, revealed, wrongTried, status.state, goNext]);

  /* ------------------------------------------------------------------ */
  /* Render                                                              */
  /* ------------------------------------------------------------------ */

  if (total === 0) {
    return (
      <>
        <AppHeader title={title} subtitle={subtitle} />
        <main id="main" className="app-main">
          <EmptyState
            message={emptyMessage ?? t('noQuestionsYet')}
            action={
              <button type="button" className="btn btn--primary" onClick={home}>
                {t('goHome')}
              </button>
            }
          />
        </main>
      </>
    );
  }

  if (finished || !question) {
    return (
      <SummaryScreen
        mode={mode}
        title={title}
        total={total}
        correct={correctCount}
        wrong={wrongCount}
        passThreshold={config.passThreshold}
        results={results}
        questions={questions}
      />
    );
  }

  const answerState = (i: number): AnswerState => {
    if (revealed) {
      if (i === question.correctIndex) return 'correct';
      if (i === selected) return 'wrong';
      return 'dim';
    }
    if (wrongTried.includes(i)) return 'wrong';
    if (selected === i) return 'selected';
    return 'idle';
  };

  /** A choice is locked once the answer is revealed, once it is solved, or once
   *  it has already been tried and was wrong (Learning Mode). */
  const answerDisabled = (i: number): boolean =>
    revealed || solved || (config.allowRetry && wrongTried.includes(i));

  const liveMessage = revealed
    ? solved
      ? t('correct')
      : `${t('incorrect')} — ${t('correctAnswerIs')} ${letters[question.correctIndex]}`
    : wrongTried.length > 0
      ? t('tryAgainMsg')
      : '';

  const praise = uiLang === 'ar' ? PRAISE_AR[index % PRAISE_AR.length] : PRAISE_EN[index % PRAISE_EN.length];
  const category = CATEGORY_BY_ID[question.category];

  return (
    <>
      <AppHeader
        title={title}
        subtitle={settings.easyMode ? undefined : subtitle}
        onBack={() => saveSession(false)}
      />
      <main id="main" className="app-main stack">
        <VoiceWarning />

        <div className="stack-sm">
          <ProgressBar
            value={index + 1}
            max={total}
            label={`${t('questionOf')} ${index + 1} ${t('ofWord')} ${total}`}
          />
          {/* The practice test always tracks correct / wrong / remaining. In easy
              mode the labels are dropped so it stays a short, calm row of icons —
              the numbers are still announced in full to screen readers. */}
          {config.showCounters ? (
            <div
              className="row"
              style={{ fontSize: settings.easyMode ? '1.05rem' : '0.85rem', fontWeight: 700 }}
            >
              <span style={{ color: 'var(--success)' }}>
                <span aria-hidden="true">✅ {settings.easyMode ? '' : `${t('correctCount')}: `}{correctCount}</span>
                <span className="visually-hidden">
                  {t('correctCount')}: {correctCount}
                </span>
              </span>
              <span style={{ color: 'var(--danger)' }}>
                <span aria-hidden="true">❌ {settings.easyMode ? '' : `${t('wrongCount')}: `}{wrongCount}</span>
                <span className="visually-hidden">
                  {t('wrongCount')}: {wrongCount}
                </span>
              </span>
              <span style={{ color: 'var(--text-muted)' }}>
                <span aria-hidden="true">⏳ {settings.easyMode ? '' : `${t('remaining')}: `}{total - index - 1}</span>
                <span className="visually-hidden">
                  {t('remaining')}: {total - index - 1}
                </span>
              </span>
            </div>
          ) : null}
          {!settings.easyMode ? (
            <div className="row">
              <span className="badge">
                <span aria-hidden="true">{category?.icon}</span>
                {uiLang === 'ar' ? category?.nameAr : category?.nameEn}
              </span>
            </div>
          ) : null}
        </div>

        <section className="card question-card" aria-labelledby="question-text">
          {question.image ? (
            <QuestionImage src={question.image.src} altAr={question.image.altAr} altEn={question.image.altEn} />
          ) : null}

          <div className="question-head">
            <SpeakButton
              id="question"
              text={question.questionAr}
              textEn={question.questionEn}
              size="lg"
              label={t('listenQuestion')}
            />
            <h2 className="question-text" id="question-text">
              <Bi
                ar={question.questionAr}
                en={question.questionEn}
                as="span"
                enClassName="question-text--en"
              />
            </h2>
          </div>

          <p style={{ margin: 0, color: 'var(--text-muted)', fontWeight: 600 }}>
            {t('chooseAnswer')} · {t('tapSpeakerHint')}
          </p>

          <AudioControls items={sequence} hideSpeed={settings.easyMode && config.autoPlayAll === false} />

          <ul className="answer-list">
            {question.answersEn.map((en, i) => (
              <AnswerButton
                key={`${question.id}-${i}`}
                index={i}
                textAr={question.answersAr[i]}
                textEn={en}
                state={answerState(i)}
                disabled={answerDisabled(i)}
                onSelect={() => handleSelect(i)}
              />
            ))}
          </ul>

          <div aria-live="assertive" className="visually-hidden">
            {liveMessage}
          </div>

          {/* Feedback */}
          {revealed && solved ? (
            <div className="feedback feedback--correct">
              <span className="feedback__icon" aria-hidden="true">
                ✅
              </span>
              <span>
                {t('correct')} — {praise}
              </span>
            </div>
          ) : null}

          {revealed && !solved ? (
            <div className="feedback feedback--wrong">
              <span className="feedback__icon" aria-hidden="true">
                ❌
              </span>
              <span>
                {t('incorrect')} — {t('correctAnswerIs')} {letters[question.correctIndex]}
              </span>
            </div>
          ) : null}

          {!revealed && wrongTried.length > 0 ? (
            <div className="feedback feedback--wrong">
              <span className="feedback__icon" aria-hidden="true">
                ❌
              </span>
              <span>{t('tryAgainMsg')}</span>
            </div>
          ) : null}

          {/* Why? */}
          {config.showExplanation && question.explanationAr && (wrongTried.length > 0 || revealed) ? (
            <div className="stack-sm">
              <button
                type="button"
                className="btn"
                aria-expanded={showExplanation}
                onClick={() => setShowExplanation((v) => !v)}
              >
                <span className="btn__icon" aria-hidden="true">
                  💡
                </span>
                {t('why')}
              </button>
              {showExplanation ? (
                <div className="explanation">
                  <div className="explanation__title">
                    <span aria-hidden="true">💡 </span>
                    {t('explanation')}
                  </div>
                  <div className="row" style={{ alignItems: 'flex-start' }}>
                    <SpeakButton
                      id="explanation"
                      text={question.explanationAr ?? ''}
                      textEn={question.explanationEn ?? ''}
                      label={t('listenExplanation')}
                    />
                    <div style={{ flex: 1, minWidth: '12rem' }}>
                      <Bi
                        ar={question.explanationAr ?? ''}
                        en={question.explanationEn ?? ''}
                        as="div"
                        enClassName="answer__en"
                      />
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          {/* Actions */}
          <div className="row">
            {config.requireSubmit && !revealed ? (
              <button
                type="button"
                className="btn btn--primary btn--big btn--block"
                onClick={handleSubmit}
                disabled={selected === null}
              >
                {t('submitAnswer')}
              </button>
            ) : null}

            {revealed ? (
              <button type="button" className="btn btn--primary btn--big btn--block" onClick={goNext}>
                {index + 1 >= total ? t('finish') : t('next')}
                <span className="btn__icon" aria-hidden="true">
                  {' '}
                  ›
                </span>
              </button>
            ) : null}

            {!revealed && !config.requireSubmit && wrongTried.length > 0 ? (
              <button type="button" className="btn btn--block" onClick={() => speakSequence(sequence)}>
                <span className="btn__icon" aria-hidden="true">
                  🔊
                </span>
                {t('listenAgain')}
              </button>
            ) : null}
          </div>
        </section>

        {!settings.easyMode ? (
          <details className="card card--flat">
            <summary style={{ cursor: 'pointer', fontWeight: 700 }}>{t('keyboardHelp')}</summary>
            <ul style={{ margin: 'var(--space-3) 0 0', paddingInlineStart: '1.2rem' }}>
              <li>{t('keyAnswer')}</li>
              <li>{t('keyRead')}</li>
              <li>{t('keyPause')}</li>
              <li>{t('keyStop')}</li>
              <li>{t('keyNext')}</li>
            </ul>
          </details>
        ) : null}
      </main>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Summary                                                             */
/* ------------------------------------------------------------------ */

function SummaryScreen({
  mode,
  title,
  total,
  correct,
  wrong,
  passThreshold,
  results,
  questions,
}: {
  mode: QuizMode;
  title: string;
  total: number;
  correct: number;
  wrong: number;
  passThreshold?: number;
  results: AnswerRecord[];
  questions: Question[];
}) {
  const t = useT();
  const { uiLang, settings } = useSettings();
  const { home, navigate } = useRouter();
  const [showReview, setShowReview] = useState(false);

  const isTest = mode === 'test';
  const threshold = passThreshold ?? Math.ceil(total * 0.75);
  const passed = correct >= threshold;
  const percent = total > 0 ? Math.round((correct / total) * 100) : 0;

  return (
    <>
      <AppHeader title={title} />
      <main id="main" className="app-main stack">
        <section className="card stack" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', lineHeight: 1 }} aria-hidden="true">
            {isTest ? (passed ? '🎉' : '💪') : '🎉'}
          </div>

          {isTest ? (
            <h2 style={{ fontSize: '2rem', color: passed ? 'var(--success)' : 'var(--warn)' }}>
              {passed ? t('passed') : t('keepPracticing')}
            </h2>
          ) : (
            <h2 style={{ fontSize: '1.7rem' }}>{t('greatJob')}</h2>
          )}

          <div style={{ fontSize: '2.6rem', fontWeight: 800 }}>
            <Fraction value={correct} max={total} />
          </div>
          <div style={{ color: 'var(--text-muted)', fontWeight: 700 }}>
            {t('yourScore')}: {percent}%
          </div>
          {isTest ? <div className="badge">{t('passingScore')}</div> : null}

          <ProgressBar value={correct} max={total} tone={passed ? 'success' : 'primary'} />

          {!settings.easyMode ? (
            <div className="row" style={{ justifyContent: 'center' }}>
              <span className="badge badge--learned">
                ✅ {t('correctCount')}: {correct}
              </span>
              <span className="badge badge--practice">
                ❌ {t('wrongCount')}: {wrong}
              </span>
            </div>
          ) : null}

          {/* The most useful thing after any session is to work on what went
              wrong, so that gets the big button when there is anything to fix.
              Not after a sign test though: "my mistakes" is a question drill and
              would send the learner somewhere unrelated. */}
          {wrong > 0 && mode !== 'signs-test' ? (
            <button
              type="button"
              className="btn btn--primary btn--big btn--block"
              onClick={() => navigate({ name: 'quiz', mode: 'mistakes' })}
            >
              <span className="btn__icon" aria-hidden="true">
                🔁
              </span>
              {t('myMistakes')}
            </button>
          ) : null}

          <div className="row" style={{ justifyContent: 'center' }}>
            <button
              type="button"
              className={wrong > 0 && mode !== 'signs-test' ? 'btn btn--big' : 'btn btn--primary btn--big'}
              onClick={home}
            >
              {t('goHome')}
            </button>
            {!settings.easyMode && results.length > 0 ? (
              <button type="button" className="btn" onClick={() => setShowReview((v) => !v)}>
                {t('reviewAnswers')}
              </button>
            ) : null}
          </div>
        </section>

        {showReview ? (
          <section className="card stack-sm">
            <h3 className="card__title">{t('reviewAnswers')}</h3>
            {results.map((r) => {
              const q = questions.find((item) => item.id === r.questionId);
              if (!q) return null;
              const letters = uiLang === 'ar' ? ARABIC_LETTERS : ENGLISH_LETTERS;
              return (
                <div key={r.questionId} className="notice" style={{ borderInlineStartColor: r.correct ? 'var(--success)' : 'var(--danger)' }}>
                  <div style={{ fontWeight: 700 }}>
                    <span aria-hidden="true">{r.correct ? '✅ ' : '❌ '}</span>
                    <Bi ar={q.questionAr} en={q.questionEn} as="span" enClassName="answer__en" />
                  </div>
                  <div style={{ fontSize: '0.85rem', marginTop: 'var(--space-2)' }}>
                    <strong>{t('correctAnswerIs')}</strong>{' '}
                    {letters[q.correctIndex]}.{' '}
                    <Bi
                      ar={q.answersAr[q.correctIndex]}
                      en={q.answersEn[q.correctIndex]}
                      as="span"
                      enClassName="answer__en"
                    />
                  </div>
                  {r.chosenIndex !== null && !r.correct ? (
                    <div style={{ fontSize: '0.85rem', color: 'var(--danger)' }}>
                      <strong>{t('yourAnswer')}:</strong> {letters[r.chosenIndex]}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </section>
        ) : null}
      </main>
    </>
  );
}
